import { buildSimpleMetricLegendLabelHtml } from "../../../utils/chart-legend-scale.util";
import Highcharts from "../highcharts.config";

/**
 * Format number with commas
 */
const formatNumber = (value: number) => {
  return value
    .toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
    .replace(/\.00$/, "");
};

export const scatterChartDefaults: Highcharts.Options = {
  chart: {
    type: "scatter",
    animation: true,
    style: {
      fontFamily: "var(--font-family, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)",
    },
    backgroundColor: "transparent",
    spacing: [10, 20, 10, 20],
    zooming: {
      type: "xy",
    },
  },
  title: {
    text: undefined,
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    title: {
      text: "",
      style: {
        color: "var(--text-primary-color)",
      },
    },
    labels: {
      style: {
        color: "var(--text-secondary-color)",
      },
    },
    gridLineWidth: 1,
    gridLineColor: "var(--border-secondary-color)",
    gridLineDashStyle: "Dot",
    tickLength: 0,
    lineColor: "var(--border-primary-color)",
  },
  yAxis: {
    title: {
      text: "",
      style: {
        color: "var(--text-primary-color)",
      },
    },
    labels: {
      style: {
        color: "var(--text-secondary-color)",
      },
    },
    gridLineWidth: 1,
    gridLineColor: "var(--border-secondary-color)",
    gridLineDashStyle: "Dot",
    lineColor: "var(--border-primary-color)",
  },
  plotOptions: {
    scatter: {
      marker: {
        radius: 6,
        symbol: "circle",
        states: {
          hover: {
            enabled: true,
            lineColor: "rgba(100, 100, 100, 0.5)",
            lineWidth: 2,
          },
        },
      },
      states: {
        hover: {
          enabled: true,
        },
        inactive: {
          opacity: 0.5,
        },
      },
      dataLabels: {
        enabled: false,
        format: "{point.name}",
        style: {
          color: "var(--text-primary-color)",
          textOutline: "none",
          fontSize: "11px",
        },
      },
    },
    series: {
      turboThreshold: 5000,
    },
  },
  tooltip: {
    outside: true,
    enabled: true,
    useHTML: true,
    shadow: {
      color: "var(--card-shadow-color)",
      opacity: 1,
      offsetX: 0,
      offsetY: 4,
      width: 12,
    },
    backgroundColor: "var(--background-primary-color)",
    borderColor: "var(--border-primary-color)",
    borderWidth: 1,
    borderRadius: 8,
    style: {
      color: "var(--text-primary-color)",
      fontSize: "var(--font-size-sm)",
    },
    formatter: function (this: any) {
      const point = this.point;
      const chart = point.series.chart;
      
      // Get axis titles from the chart
      const xAxisTitle = chart.xAxis?.[0]?.options?.title?.text || "X";
      const yAxisTitle = chart.yAxis?.[0]?.options?.title?.text || "Y";
      
      const pointName = point.name 
        ? `<div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: var(--text-secondary-color); margin-bottom: 8px;">${point.name}</div>` 
        : "";
      
      return `
        <div style="padding: 8px; background: var(--background-primary-color);">
          ${pointName}
          <div style="display: flex; align-items: center;">
            <div style="width: 3px; height: 40px; background-color: ${point.color}; margin-right: 8px; border-radius: 2px;"></div>
            <div style="display: flex; flex-direction: column;">
              <div style="display: flex; gap: 16px;">
                <span style="color: var(--text-primary-color); font-weight: var(--font-weight-regular); font-size: var(--font-size-sm);">${xAxisTitle}: ${formatNumber(point.x)}</span>
                <span style="color: var(--text-primary-color); font-weight: var(--font-weight-regular); font-size: var(--font-size-sm);">${yAxisTitle}: ${formatNumber(point.y)}</span>
              </div>
            </div>
          </div>
        </div>
      `;
    },
  },
  legend: {
    enabled: false,
    align: "left",
    verticalAlign: "top",
    layout: "horizontal",
    useHTML: true,
    itemStyle: {
      color: "var(--text-primary-color)",
      fontWeight: "normal",
      fontSize: "var(--font-size-md)",
    },
    itemHoverStyle: {
      color: "var(--text-primary-color)",
    },
    symbolWidth: 0,
    symbolHeight: 0,
    symbolRadius: 0,
    symbolPadding: 0,
    squareSymbol: false,
    itemDistance: 24,
    itemMarginBottom: 0,
    navigation: {
      activeColor: "var(--text-link-color)",
      inactiveColor: "var(--text-secondary-color)",
      animation: true,
    },
    labelFormatter: function (this: any) {
      const series = this;
      const pointCount = series.data?.length || 0;
      const options = (series as any).userOptions || {};
      const aggregationValue = options.aggregationValue;

      return buildSimpleMetricLegendLabelHtml({
        seriesName: series.name,
        color: series.color,
        primaryText:
          aggregationValue !== undefined
            ? formatNumber(aggregationValue)
            : String(pointCount),
        secondaryText:
          aggregationValue !== undefined ? undefined : "points",
      });
    },
  },
};
